import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { format, parse, isValid, isAfter, isBefore, startOfDay } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@/utils/dateFormat';
import 'react-day-picker/style.css';

/**
 * DatePickerSingle Component
 *
 * Single date selection with European format (dd.mm.yyyy)
 * Returns ISO string for API compatibility
 * Optional time input if showTime prop is true
 * Uses Radix Popover + react-day-picker for calendar
 */

interface DatePickerSingleProps {
  value: string; // ISO string or empty
  onChange: (isoDate: string) => void;
  label?: string;
  placeholder?: string;
  showTime?: boolean;
  disabled?: boolean;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
}

interface TimeState {
  hours: string;
  minutes: string;
}

const DEFAULT_PLACEHOLDER = 'Select date';
const EUROPEAN_DATE_FORMAT = 'dd.MM.yyyy';
const EUROPEAN_DATETIME_FORMAT = 'dd.MM.yyyy HH:mm';

export const DatePickerSingle = ({
  value,
  onChange,
  label,
  placeholder = DEFAULT_PLACEHOLDER,
  showTime = false,
  disabled = false,
  className = '',
  minDate,
  maxDate,
}: DatePickerSingleProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isInvalidDate, setIsInvalidDate] = useState(false);
  const [isHoveringInput, setIsHoveringInput] = useState(false);
  const [timeState, setTimeState] = useState<TimeState>({ hours: '00', minutes: '00' });
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Parse ISO string to Date object
   */
  const parseISOToDate = useCallback((isoString: string): Date | null => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return isValid(date) ? date : null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Current selected date as Date object
   */
  const selectedDate = useMemo(() => parseISOToDate(value), [value, parseISOToDate]);

  /**
   * Initialize input value and time from prop value
   */
  useEffect(() => {
    if (value && selectedDate) {
      const formatString = showTime ? EUROPEAN_DATETIME_FORMAT : EUROPEAN_DATE_FORMAT;
      setInputValue(format(selectedDate, formatString));

      if (showTime) {
        setTimeState({
          hours: format(selectedDate, 'HH'),
          minutes: format(selectedDate, 'mm'),
        });
      }
    } else {
      setInputValue('');
      setTimeState({ hours: '00', minutes: '00' });
    }
  }, [value, selectedDate, showTime]);

  /**
   * Parse European date format (dd.mm.yyyy) to Date
   */
  const parseEuropeanDate = useCallback((dateString: string): Date | null => {
    const cleaned = dateString.trim();

    // Try dd.MM.yyyy format
    const parsedDate = parse(cleaned, EUROPEAN_DATE_FORMAT, new Date());
    if (isValid(parsedDate)) {
      return parsedDate;
    }

    // Try dd.MM.yyyy HH:mm format
    if (showTime) {
      const parsedDateTime = parse(cleaned, EUROPEAN_DATETIME_FORMAT, new Date());
      if (isValid(parsedDateTime)) {
        return parsedDateTime;
      }
    }

    return null;
  }, [showTime]);

  /**
   * Validate date against min/max constraints
   */
  const isDateInRange = useCallback((date: Date): boolean => {
    const dateOnly = startOfDay(date);

    if (minDate && isBefore(dateOnly, startOfDay(minDate))) {
      return false;
    }

    if (maxDate && isAfter(dateOnly, startOfDay(maxDate))) {
      return false;
    }

    return true;
  }, [minDate, maxDate]);

  /**
   * Combine date and time into ISO string
   */
  const combineDateAndTime = useCallback((date: Date, time: TimeState): string => {
    const hours = parseInt(time.hours, 10) || 0;
    const minutes = parseInt(time.minutes, 10) || 0;

    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);

    return combined.toISOString();
  }, []);

  /**
   * Convert date to ISO string for date-only selections
   * Uses noon (12:00) to avoid timezone shift issues
   */
  const dateOnlyToISO = useCallback((date: Date): string => {
    const dateAtNoon = new Date(date);
    dateAtNoon.setHours(12, 0, 0, 0);
    return dateAtNoon.toISOString();
  }, []);

  /**
   * Handle manual input change
   */
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsInvalidDate(false);
  }, []);

  /**
   * Handle input blur - parse and validate date
   */
  const handleInputBlur = useCallback(() => {
    if (!inputValue.trim()) {
      onChange('');
      setIsInvalidDate(false);
      return;
    }

    const parsedDate = parseEuropeanDate(inputValue);

    if (!parsedDate) {
      setIsInvalidDate(true);
      return;
    }

    if (!isDateInRange(parsedDate)) {
      setIsInvalidDate(true);
      return;
    }

    setIsInvalidDate(false);

    if (showTime) {
      onChange(combineDateAndTime(parsedDate, timeState));
    } else {
      onChange(dateOnlyToISO(parsedDate));
    }
  }, [inputValue, parseEuropeanDate, isDateInRange, showTime, onChange, combineDateAndTime, dateOnlyToISO, timeState]);

  /**
   * Handle calendar day selection
   */
  const handleDaySelect = useCallback((date: Date | undefined) => {
    if (!date) return;

    if (!isDateInRange(date)) {
      return;
    }

    if (showTime) {
      onChange(combineDateAndTime(date, timeState));
    } else {
      onChange(dateOnlyToISO(date));
    }

    if (!showTime) {
      setIsOpen(false);
    }
  }, [isDateInRange, showTime, onChange, combineDateAndTime, dateOnlyToISO, timeState]);

  /**
   * Handle time input change
   */
  const handleHoursChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const hours = Math.min(23, Math.max(0, parseInt(val, 10) || 0));
    const newTimeState = { ...timeState, hours: hours.toString().padStart(2, '0') };
    setTimeState(newTimeState);

    if (selectedDate) {
      onChange(combineDateAndTime(selectedDate, newTimeState));
    }
  }, [timeState, selectedDate, onChange, combineDateAndTime]);

  const handleMinutesChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const minutes = Math.min(59, Math.max(0, parseInt(val, 10) || 0));
    const newTimeState = { ...timeState, minutes: minutes.toString().padStart(2, '0') };
    setTimeState(newTimeState);

    if (selectedDate) {
      onChange(combineDateAndTime(selectedDate, newTimeState));
    }
  }, [timeState, selectedDate, onChange, combineDateAndTime]);

  /**
   * Clear date
   */
  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setInputValue('');
    setIsInvalidDate(false);
    setTimeState({ hours: '00', minutes: '00' });
    setIsOpen(false);
  }, [onChange]);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
      setIsOpen(true);
    } else if (e.key === 'Escape') {
      // Only consume the key when it closes the open calendar; otherwise a surrounding drawer/modal may close.
      if (isOpen) e.preventDefault();
      setIsOpen(false);
      inputRef.current?.blur();
    }
  }, [handleInputBlur, isOpen]);

  /**
   * Disabled days matcher for react-day-picker
   */
  const disabledDays = useMemo(() => {
    const matchers: Array<{ before: Date } | { after: Date }> = [];

    if (minDate) {
      matchers.push({ before: startOfDay(minDate) });
    }

    if (maxDate) {
      matchers.push({ after: startOfDay(maxDate) });
    }

    return matchers.length > 0 ? matchers : undefined;
  }, [minDate, maxDate]);

  const showClearButton = !!inputValue && !disabled && isHoveringInput;

  return (
    <div className={`date-picker-single ${className}`}>
      {label && (
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {label}
        </label>
      )}

      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <div
            className="relative outline-none"
            onMouseEnter={() => setIsHoveringInput(true)}
            onMouseLeave={() => setIsHoveringInput(false)}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              aria-label={label || 'Date picker'}
              aria-invalid={isInvalidDate}
              className={[
                'w-full py-2 px-3 text-sm rounded-md border bg-white',
                'transition-colors duration-150',
                'focus:outline-none focus:border-primary-400 focus:[box-shadow:0_0_0_3px_rgba(77,182,172,0.3)]',
                showClearButton ? 'pr-16' : 'pr-8',
                isInvalidDate
                  ? 'border-red-400 text-red-900'
                  : 'border-gray-200 text-gray-900 hover:border-gray-300',
                disabled
                  ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  : '',
                !inputValue && !disabled ? 'text-gray-400' : '',
              ].filter(Boolean).join(' ')}
            />

            {/* Clear button — visible on hover when value exists */}
            {showClearButton && (
              <button
                type="button"
                onClick={handleClear}
                aria-label="Clear date"
                className="absolute right-7 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                </svg>
              </button>
            )}

            {/* Calendar icon toggle */}
            <button
              type="button"
              onClick={() => !disabled && setIsOpen(!isOpen)}
              disabled={disabled}
              aria-label="Toggle calendar"
              tabIndex={-1}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.75}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </button>
          </div>
        </Popover.Trigger>

        {isInvalidDate && (
          <p className="mt-1 text-xs text-red-500" role="alert">
            {t('datepicker_invalid_format', { format: showTime ? 'dd.mm.yyyy HH:mm' : 'dd.mm.yyyy' })}
          </p>
        )}

        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={5}
            className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-auto date-picker-compact"
            style={{ zIndex: 9999 }}
          >
            <DayPicker
              mode="single"
              selected={selectedDate || undefined}
              onSelect={handleDaySelect}
              disabled={disabledDays}
              defaultMonth={selectedDate || undefined}
              captionLayout="label"
              weekStartsOn={1}
              showWeekNumber
              locale={getDateLocale()}
            />

            {showTime && (
              <div className="mt-2 pt-2.5 border-t border-gray-100">
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1.5">
                  {t('time')}
                </p>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={timeState.hours}
                    onChange={handleHoursChange}
                    className="w-14 px-2 py-1 text-center text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400"
                    aria-label="Hours"
                  />
                  <span className="text-gray-400 text-sm font-medium">:</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={timeState.minutes}
                    onChange={handleMinutesChange}
                    className="w-14 px-2 py-1 text-center text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400"
                    aria-label="Minutes"
                  />
                </div>
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
